import type { ReactNode } from "react";
import { defineStrings, useStrings } from "../i18n";
import { Icon, type IconName } from "./Icon";

/** The kind of small moment, which picks the icon. The app maps engine milestones to it. */
export type MomentKind = "step" | "harder" | "streak" | "review";

const icons: Record<MomentKind, IconName> = {
  step: "up",
  // A summit, not the sparkle: eight rays read as a loading spinner at toast size.
  harder: "peak",
  streak: "star",
  review: "heart",
};

const strings = defineStrings({ dismiss: "Close" }, { dismiss: "Fermer" });

/**
 * A small moment inside a skill (a step reached, a harder item, a streak, a review kept),
 * shown only when the engine reports one. Inline and non-modal, announced politely, and
 * never on a timer: it stays until the app replaces it or the child closes it. Copy is
 * about effort, never a percentage. Mastery itself uses `Celebration`.
 */
export function MomentToast(props: {
  kind: MomentKind;
  title: string;
  children?: ReactNode;
  onDismiss?: () => void;
}) {
  const t = useStrings(strings);
  return (
    <div className="ui-moment" data-kind={props.kind} role="status">
      <span className="ui-moment__icon">
        <Icon name={icons[props.kind]} size={22} />
      </span>
      <div className="ui-moment__text">
        <p className="ui-moment__title">{props.title}</p>
        {props.children !== undefined && <div className="ui-moment__body">{props.children}</div>}
      </div>
      {props.onDismiss !== undefined && (
        <button
          type="button"
          className="ui-moment__close"
          aria-label={t("dismiss")}
          onClick={props.onDismiss}
        >
          <Icon name="close" size={18} />
        </button>
      )}
    </div>
  );
}
